import React, { useEffect, useState } from "react";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { showModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";

const showErrorMessage = (error) => (
  <span className={`input__error__message ${error !== "" ? "show" : ""}`}>
    {error}
  </span>
);

const AuthenticationSSO = ({ enabled }) => {
  const dispatch = useDispatch();

  const currentId = useSelector((state) => state.systemAdmin.sso.id);
  const [id, setId] = useState(currentId);
  const [idError, setIdError] = useState("");

  const currentUrl = useSelector((state) => state.systemAdmin.sso.url);
  const [url, setURL] = useState(currentUrl);
  const [urlError, setURLError] = useState("");

  const currentCert = useSelector((state) => state.systemAdmin.sso.cert);
  const [cert, setCert] = useState(currentCert);
  const [certError, setCertError] = useState("");

  const [tested, setTested] = useState(false);

  const notEmptyField = "This field cannot be empty";

  const buttonDisabled = () =>
    idError !== "" ||
    urlError !== "" ||
    certError !== "" ||
    !id ||
    !url ||
    !cert ||
    id?.length === 0 ||
    url?.length === 0 ||
    cert?.length === 0;

  const encodeParams = (params) =>
    Object.entries(params)
      .map((kv) => kv.map(encodeURIComponent).join("="))
      .join("&");

  const testSSO = () => {
    const params = encodeParams({
      entityId: id,
      idpUrl: url,
      cert,
    });
    window.open(`/saml/test?${params}`, "_blank");
    setTested(true);
  };

  useEffect(() => {
    setId(currentId);
  }, [currentId]);

  useEffect(() => {
    setURL(currentUrl);
  }, [currentUrl]);

  useEffect(() => {
    setCert(currentCert);
  }, [currentCert]);

  useEffect(() => {
    setIdError(id?.length === 0 ? notEmptyField : "");
    setURLError(url?.length === 0 ? notEmptyField : "");
    setCertError(cert?.length === 0 ? notEmptyField : "");
  }, [id, url, cert]);

  return (
    <div className="authentication__content__body__sso">
      {enabled ? null : (
        <div className="authentication__content__body__sso__disabled" />
      )}
      <header className="title">SSO Settings</header>
      <div className="scrollable">
        <div className="authentication__content__body__sso__title">
          <span>
            Use your companys identity provider service to automatically log
            into VT Docs.
          </span>
          <span>
            You will need to provide a valid Identity Provider ID, URL and a
            valid Public x509 certificate. You can test your SAML settings
            before finalising your changes.
          </span>
        </div>
        <div className="authentication__content__body__sso__config">
          <header className="title">Add SAML Configuration</header>
          <div>
            <Button
              onClick={() =>
                window.open(
                  `https://${window.location.host}/saml/metadata`,
                  "_blank"
                )
              }
              content="Download Meta Data"
              variant={buttonVariants.borderless}
            />
            <Button
              onClick={() =>
                window.open(
                  `https://support.visiblethread.com/hc/en-us/articles/360038695431`,
                  "_blank"
                )
              }
              content="Read Support Article"
              variant={buttonVariants.borderless}
            />
          </div>
        </div>
        <div className="authentication__content__body__sso__idp-id">
          <header className="title">Identity Provider Entity ID</header>
          <input
            type="text"
            id="authentication__content__body__sso__idp-id"
            placeholder="The URL of your identity"
            value={id}
            onChange={(e) => {
              setId(e.target.value);
              setTested(false);
            }}
            className={idError !== "" ? "input__error" : ""}
          />
          {showErrorMessage(idError)}
        </div>
        <div className="authentication__content__body__sso__idp-url">
          <header className="title">Identity Provider SSO URL</header>
          <input
            type="text"
            id="authentication__content__body__sso__idp-url"
            placeholder="www."
            value={url}
            onChange={(e) => {
              setURL(e.target.value);
              setTested(false);
            }}
            className={urlError !== "" ? "input__error" : ""}
          />
          {showErrorMessage(urlError)}
        </div>
        <div className="authentication__content__body__sso__idp-cert">
          <header className="title">Public x509 Certificate</header>
          <textarea
            id="authentication__content__body__sso__idp-cert"
            placeholder="Copy and paste code here"
            value={cert}
            onChange={(e) => {
              setCert(e.target.value);
              setTested(false);
            }}
            className={certError !== "" ? "input__error" : ""}
          />
          {showErrorMessage(certError)}
        </div>
      </div>
      <div className="authentication__content__body__sso__idp-buttons">
        <Button
          dataTest="testSettings"
          onClick={testSSO}
          variant={buttonVariants.secondary}
          content="Test Login"
          disabled={buttonDisabled()}
        />
        <Button
          dataTest="saveSettings"
          onClick={() =>
            dispatch(showModal(modalTypes.ENABLE_SSO, { id, url, cert }))
          }
          variant={buttonVariants.primary}
          content="Save Settings"
          disabled={buttonDisabled() || !tested || !enabled}
        />
      </div>
    </div>
  );
};

export default AuthenticationSSO;

AuthenticationSSO.propTypes = {
  enabled: PropTypes.bool.isRequired,
};
